import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { streamingImportService } from '../../services/streamingImportService';
import ColumnMappingInterface from './ColumnMappingInterface';
import ValidationFeedback from './ValidationFeedback';

type ImportStatus = {
  status?: string;
  progress?: {
    percentComplete?: number;
  };
  summary?: {
    totalRows?: number;
    successfulRows?: number;
    failedRows?: number;
    warnings?: number;
  };
  message?: string;
};

/**
 * Streaming CSV Upload component for handling large files
 * Uses streaming upload with progress tracking
 */
const StreamingCSVUploadPage: React.FC = () => {
  // auth context is available but not currently used
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const auth = useAuth();
  
  // State variables
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importId, setImportId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [processingProgress, setProcessingProgress] = useState<number>(0);
  const [sampleData, setSampleData] = useState<string[][]>([]);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [importType, setImportType] = useState<'sales_data' | 'ad_spend_data'>('sales_data');
  const [currentStep, setCurrentStep] = useState<'select' | 'upload' | 'map' | 'process' | 'complete' | 'error'>('select');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [validationResults, setValidationResults] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [importSummary, setImportSummary] = useState<Record<string, unknown> | null>(null);

  // Set auth token on component mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      streamingImportService.setAuthToken(token);
    }
  }, []);
  
  /**
   * Handle file selection
   */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    
    if (file) {
      // Reset states when a new file is selected
      setImportId(null);
      setUploadProgress(0);
      setProcessingProgress(0);
      setSampleData([]);
      setColumnMapping({});
      setValidationResults(null);
      setErrorMessage('');
      setImportSummary(null);
    }
  };
  
  /**
   * Handle import type selection
   */
  const handleImportTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setImportType(event.target.value as 'sales_data' | 'ad_spend_data');
  };
  
  /**
   * Initialize import process
   */
  const handleInitializeImport = async () => {
    if (!selectedFile) {
      setErrorMessage('Please select a file first');
      return;
    }
    
    setIsLoading(true);
    setCurrentStep('upload');
    
    try {
      const result = await streamingImportService.initializeImport(
        importType,
        selectedFile.name,
        selectedFile.size
      );
      
      if (typeof result.importId === 'string') {
        setImportId(result.importId);
        await handleFileUpload(result.importId);
      } else {
        throw new Error('Invalid import ID received from server');
      }
    } catch (error) {
      console.error('Error initializing import:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to initialize import');
      setCurrentStep('error');
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * Upload file with progress tracking
   */
  const handleFileUpload = async (uploadImportId: string) => {
    if (!selectedFile) {
      setErrorMessage('No file selected');
      return;
    }
    
    try {
      const result = await streamingImportService.uploadFile(
        selectedFile, 
        uploadImportId,
        (progress) => setUploadProgress(progress)
      );
      
      if (Array.isArray(result.sampleData)) {
        setSampleData(result.sampleData);
      } else {
        setSampleData([]);
      }
      setCurrentStep('map');
    } catch (error) {
      console.error('Error uploading file:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to upload file');
      setCurrentStep('error');
    }
  };
  
  /**
   * Submit column mapping
   */
  const handleSubmitMapping = async () => {
    if (!importId) {
      setErrorMessage('No active import session');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const result = await streamingImportService.submitColumnMapping(importId, columnMapping);
      
      if (result.validationSummary) {
        setValidationResults(result.validationSummary);
      }
      
      setCurrentStep('process');
    } catch (error) {
      console.error('Error submitting column mapping:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to submit column mapping');
      setCurrentStep('error');
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * Start processing the file
   */
  const handleStartProcessing = async () => {
    if (!importId) {
      setErrorMessage('No active import session');
      return;
    }
    
    setIsProcessing(true);
    
    try {
      await streamingImportService.startProcessing(importId);
      
      // Start polling for status updates
      setIsPolling(true);
      streamingImportService.pollUntilComplete(
        importId,
        (status: ImportStatus) => {
          if (status.progress && typeof status.progress.percentComplete === 'number') {
            setProcessingProgress(status.progress.percentComplete);
          }
          
          if (status.status === 'completed') {
            setImportSummary(status.summary || {});
            setCurrentStep('complete');
            setIsPolling(false);
            setIsProcessing(false);
          } else if (status.status === 'failed') {
            setErrorMessage(typeof status.message === 'string' ? status.message : 'Import failed');
            setCurrentStep('error');
            setIsPolling(false);
            setIsProcessing(false);
          }
        }
      ).catch(error => {
        console.error('Error during status polling:', error);
        setErrorMessage(error instanceof Error ? error.message : 'Error monitoring import progress');
        setCurrentStep('error');
        setIsPolling(false);
        setIsProcessing(false);
      });
    } catch (error) {
      console.error('Error starting processing:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to start processing');
      setCurrentStep('error');
      setIsProcessing(false);
    }
  };
  
  /**
   * Cancel an in-progress import
   */
  const handleCancelImport = async () => {
    if (!importId || !isProcessing) return;
    
    try {
      await streamingImportService.cancelImport(importId);
      setIsProcessing(false);
      setIsPolling(false);
      setErrorMessage('Import cancelled by user');
      setCurrentStep('error');
    } catch (error) {
      console.error('Error cancelling import:', error);
    }
  };
  
  // We now set the mapping directly in the onChange callback
  
  /**
   * Reset the form to start over
   */
  const handleReset = () => {
    setSelectedFile(null);
    setImportId(null);
    setUploadProgress(0);
    setProcessingProgress(0);
    setSampleData([]);
    setColumnMapping({});
    setImportType('sales_data');
    setCurrentStep('select');
    setErrorMessage('');
    setValidationResults(null);
    setIsProcessing(false);
    setIsLoading(false);
    setIsPolling(false);
    setImportSummary(null);
  };

  // Render progress bar
  const renderProgressBar = (value: number, label: string) => (
    <div className="w-full bg-gray-200 rounded-full h-4 my-4">
      <div 
        className="bg-blue-600 h-4 rounded-full transition-all duration-300 ease-in-out"
        style={{ width: `${value}%` }}
      />
      <div className="text-xs text-center mt-1">{label}: {value}%</div>
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Streaming CSV Upload</h1>
      <p className="mb-4 text-gray-600">
        Use this page for uploading and processing large CSV files (10MB+) with real-time progress tracking.
      </p>
      
      {/* Error message display */}
      {errorMessage && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p>{errorMessage}</p>
          <button 
            className="underline text-red-700 mt-2"
            onClick={handleReset}
          >
            Start Over
          </button>
        </div>
      )}
      
      {/* Step 1: Select File */}
      {currentStep === 'select' && (
        <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
          <h2 className="text-xl font-semibold mb-4">Select CSV File</h2>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Import Type
            </label>
            <select
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              value={importType}
              onChange={handleImportTypeChange}
            >
              <option value="sales_data">Sales Data</option>
              <option value="ad_spend_data">Ad Spend Data</option>
            </select>
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              CSV File
            </label>
            <input
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
            />
            <p className="text-xs text-gray-500 mt-1">
              Select a CSV file to upload. For best performance with large files, ensure your file is properly formatted.
            </p>
          </div>
          
          <div className="flex items-center justify-between">
            <button
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              onClick={handleInitializeImport}
              disabled={!selectedFile}
            >
              {isLoading ? 'Initializing...' : 'Start Upload'}
            </button>
          </div>
        </div>
      )}
      
      {/* Step 2: Upload */}
      {currentStep === 'upload' && (
        <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
          <h2 className="text-xl font-semibold mb-4">Uploading File</h2>
          <p className="mb-4">Please wait while your file is being uploaded...</p>
          
          {renderProgressBar(uploadProgress, 'Upload Progress')}
          
          <p className="text-sm text-gray-600">
            This may take a few minutes for large files. Do not close this window during upload.
          </p>
        </div>
      )}
      
      {/* Step 3: Map Columns */}
      {currentStep === 'map' && (
        <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
          <h2 className="text-xl font-semibold mb-4">Map Columns</h2>
          
          {sampleData.length > 0 && (
            <>
              <p className="mb-4">
                Map the columns from your CSV file to the required fields:
              </p>
              
              <ColumnMappingInterface
                csvHeaders={sampleData[0] || []}
                fieldsToMap={importType === 'sales_data' 
                  ? ['lead_source', 'contract_total', 'gross_margin', 'gross_profit'] 
                  : ['lead_source', 'spend']}
                columnMappings={columnMapping}
                onChange={(field, csvHeader) => {
                  setColumnMapping({
                    ...columnMapping,
                    [field]: csvHeader
                  });
                }}
              />
              
              <div className="flex items-center justify-between mt-6">
                <button
                  className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  onClick={handleReset}
                >
                  Cancel
                </button>
                <button
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  onClick={handleSubmitMapping}
                  disabled={Object.keys(columnMapping).length === 0 || isLoading}
                >
                  {isLoading ? 'Submitting...' : 'Continue'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
      
      {/* Step 4: Process */}
      {currentStep === 'process' && (
        <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
          <h2 className="text-xl font-semibold mb-4">Process Data</h2>
          
          {validationResults && (
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-2">Validation Results</h3>
              <ValidationFeedback validationResults={validationResults} />
            </div>
          )}
          
          {!isProcessing ? (
            <div className="flex flex-col items-center">
              <p className="mb-4">
                Click the button below to start processing your data. This may take several minutes for large files.
              </p>
              <div className="flex gap-4">
                <button
                  className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  onClick={handleReset}
                >
                  Cancel
                </button>
                <button
                  className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  onClick={handleStartProcessing}
                >
                  Start Processing
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <p className="mb-4">
                Processing data... Please wait. This may take several minutes for large files.
              </p>
              
              {renderProgressBar(processingProgress, 'Processing Progress')}
              
              <button
                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline mt-4"
                onClick={handleCancelImport}
              >
                Cancel Process
              </button>
              
              <p className="text-sm text-gray-600 mt-4">
                Do not close this window while processing is in progress.
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Step 5: Complete */}
      {currentStep === 'complete' && (
        <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
          <div className="text-center mb-6">
            <svg className="w-16 h-16 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <h2 className="text-2xl font-bold text-gray-800">Processing Complete!</h2>
          </div>
          
          {importSummary && (
            <div className="bg-gray-100 p-4 rounded mb-6">
              <h3 className="text-lg font-medium mb-2">Import Summary</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Total Rows: {(importSummary as any).totalRows || 0}</li>
                <li>Successfully Processed: {(importSummary as any).successfulRows || 0}</li>
                <li>Failed Rows: {(importSummary as any).failedRows || 0}</li>
                {(importSummary as any).warnings > 0 && (
                  <li>Warnings: {(importSummary as any).warnings}</li>
                )}
              </ul>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <button
              className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              onClick={handleReset}
            >
              Upload Another File
            </button>
            <button
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              onClick={() => window.location.href = '/reports'}
            >
              View Reports
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StreamingCSVUploadPage;